import { useMemo } from "react";
import ErrorDisplay from "../../components/shared/ErrorDisplay";
import HarperLoadingSpinner from "../../components/shared/HarperLoadingSpinner";
import Typography from "../../components/shared/typography";
import { useGetCallMessages } from "../hooks/useGetCallMessages";
import { CallMessagesList } from "./CallMessagesList";

const DEFAULT_PAGE_SIZE = 200;

export function CallMessagesPanel({ callId }: { callId?: string | null }) {
  const params = useMemo(
    () => ({ limit: DEFAULT_PAGE_SIZE, offset: 0 }),
    []
  );
  const { data = [], isLoading, isError } = useGetCallMessages(
    callId ?? undefined,
    params
  );

  if (!callId) {
    return (
      <div className="h-full overflow-y-auto bg-white rounded-lg shadow-sm">
        <div className="flex flex-col space-y-4 p-4">
          <Typography variant="subtitle1">Call messages</Typography>
          <Typography variant="body2" className="text-brown-600">
            Select a call to review its messages.
          </Typography>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center bg-white rounded-lg shadow-sm">
        <HarperLoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorDisplay
        title="Unable to load call messages"
        description="Please try again in a moment."
        tone="warning"
      />
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between border-b border-cream-200 px-4 py-3">
        <Typography variant="subtitle1">Call messages</Typography>
        <Typography variant="body2" className="text-brown-600">
          {data.length} messages
        </Typography>
      </div>
      {data.length === 0 ? (
        <div className="flex flex-col space-y-4 p-4">
          <Typography variant="body2" className="text-brown-600">
            No messages recorded for this call.
          </Typography>
        </div>
      ) : (
        <CallMessagesList items={data} />
      )}
    </div>
  );
}
